import { useEffect, useState, useCallback, useMemo } from 'react';
import { Typography, Box, CircularProgress, Alert, Divider, Stack, Chip } from '@mui/material';
import DateRangeSelector from '../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../theme/pageCardSx';
import NaverRevenueTable from '../../components/naver/table/NaverRevenueTable';
import NaverCampaignTable from '../../components/naver/table/NaverCampaignTable';
import NaverDailyRevenueChart from '../../components/naver/chart/NaverDailyRevenueChart';
import PublisherFilter from '../../components/naver/PublisherFilter';
import NaverServiceFilter from '../../components/naver/NaverServiceFilter';
import apiClient from '../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import {
  NaverRevenueStats,
  NaverCampaignStats,
  NaverProviderCampaign,
} from '../../types/naver.types';

const ALL_PROVIDER = '전체';

export default function NaverProviderRevenue() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const [providerCampaigns, setProviderCampaigns] = useState<NaverProviderCampaign[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>(ALL_PROVIDER);
  const [selectedPublisher, setSelectedPublisher] = useState<string | undefined>(undefined);
  const [selectedService, setSelectedService] = useState<string | undefined>(undefined);

  /** 서비스(직연동/포인트홈) 구분은 아바티 데이터에만 존재 - 구분이 아바티일 때만 노출 */
  const showServiceFilter = selectedPublisher === 'avatye';

  const [naverRevenue, setNaverRevenue] = useState<NaverRevenueStats | null>(null);
  const [naverCampaignStats, setNaverCampaignStats] = useState<NaverCampaignStats[] | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const providerOptions = useMemo(
    () => Array.from(new Set(providerCampaigns.map((c) => c.providerID))).sort(),
    [providerCampaigns],
  );

  const selectedProviderCampaigns = useMemo(
    () =>
      providerCampaigns
        .filter((c) => c.providerID === selectedProvider)
        .map((c) => c.campaignTypeName),
    [providerCampaigns, selectedProvider],
  );

  useEffect(() => {
    apiClient.get('/api/v1/stat/naver/campaign/list').then((res) => {
      setProviderCampaigns(res.data);
    });
  }, []);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setErrorMessage('');

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      ...(selectedPublisher ? { publisher: selectedPublisher } : {}),
      ...(showServiceFilter && selectedService ? { service: selectedService } : {}),
    };

    if (selectedProvider === ALL_PROVIDER) {
      apiClient
        .get('/api/v1/stat/naver/revenue', { params })
        .then((res) => {
          setNaverRevenue(res.data);
          setNaverCampaignStats(null);
        })
        .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
        .finally(() => setLoading(false));
    } else {
      apiClient
        .get('/api/v1/stat/naver/campaign', {
          params: { ...params, provider: selectedProvider },
        })
        .then((res) => {
          setNaverCampaignStats(res.data);
          setNaverRevenue(null);
        })
        .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
        .finally(() => setLoading(false));
    }
  }, [startDate, endDate, selectedProvider, selectedPublisher, selectedService, showServiceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const revenueChartData = useMemo(() => {
    if (!naverRevenue) return [];
    return Object.entries(naverRevenue.totalRevenue).map(([date, revenue]) => ({ date, revenue }));
  }, [naverRevenue]);

  const campaignChartData = useMemo(() => {
    if (!naverCampaignStats) return [];
    const map = new Map<string, number>();
    naverCampaignStats.forEach((row) => {
      map.set(row.date, (map.get(row.date) ?? 0) + row.revenue);
    });
    return Array.from(map.entries()).map(([date, revenue]) => ({ date, revenue }));
  }, [naverCampaignStats]);

  return (
    <Box sx={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <Box display="flex" alignItems="center">
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
        />
      </Box>

      <Box sx={PAGE_CARD_SX}>
        {/* 퍼블리셔 필터 / 서비스 필터 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PublisherFilter
            selected={selectedPublisher}
            onSelect={(publisher) => {
              setSelectedPublisher(publisher);
              if (publisher !== 'avatye') setSelectedService(undefined);
            }}
          />
          {showServiceFilter && (
            <NaverServiceFilter selected={selectedService} onSelect={setSelectedService} />
          )}
        </Box>

        {/* 공급사 필터 */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {[ALL_PROVIDER, ...providerOptions].map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedProvider(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedProvider === name ? 700 : 400,
                backgroundColor: selectedProvider === name ? '#0044D2' : '#fff',
                color: selectedProvider === name ? '#fff' : '#374151',
                border: `1px solid ${selectedProvider === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedProvider === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {selectedProvider === ALL_PROVIDER && naverRevenue && startDate && endDate && (
              <>
                <NaverDailyRevenueChart data={revenueChartData} />
                <NaverRevenueTable
                  data={naverRevenue}
                  providerCampaigns={providerCampaigns}
                  startDate={startDate}
                  endDate={endDate}
                />
              </>
            )}
            {selectedProvider !== ALL_PROVIDER && naverCampaignStats && startDate && endDate && (
              <>
                <NaverDailyRevenueChart data={campaignChartData} />
                <NaverCampaignTable
                  data={naverCampaignStats}
                  campaigns={selectedProviderCampaigns}
                  provider={selectedProvider}
                  startDate={startDate}
                  endDate={endDate}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
